import { supabase } from './supabase';

function isDirectBrowserUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const host = new URL(url).hostname;
    if (host.includes('storage.bunnycdn.com')) return false;
    return true;
  } catch {
    return false;
  }
}

function isStlPath(filePath, fileName) {
  const n = String(fileName || filePath || '').toLowerCase();
  return n.endsWith('.stl');
}

/** Bunny HTTP / clés stockage : téléchargement via Edge Function (AccessKey côté serveur). */
function shouldDownloadViaProxy(filePath) {
  if (!filePath || typeof filePath !== 'string') return true;
  if (isDirectBrowserUrl(filePath)) return false;
  try {
    const host = new URL(filePath).hostname;
    if (host.includes('storage.bunnycdn.com') || host.includes('b-cdn.net')) {
      return true;
    }
  } catch {
    /* chemin relatif uploads/... */
  }
  return true;
}

function triggerBlobDownload(blob, fileName) {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'download';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
}

async function fetchAuthenticatedBlob(functionName, filePath, fileName, extraParams = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }

  const params = new URLSearchParams({
    file_path: filePath,
    file_name: fileName || filePath.split('/').pop() || 'file',
    ...extraParams,
  });

  const response = await fetch(
    `${supabase.supabaseUrl}/functions/v1/${functionName}?${params}`,
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabase.supabaseKey,
      },
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Erreur téléchargement (${response.status})`);
  }

  const blob = await response.blob();
  if (!blob.size) {
    throw new Error('Le fichier reçu est vide.');
  }
  return blob;
}

/**
 * Télécharge un fichier cas (STL / Bunny / S3 signé).
 * @param {{ filePath: string, fileName?: string }} opts
 */
export async function downloadCaseStorageFile({ filePath, fileName }) {
  const path = filePath?.trim();
  if (!path) throw new Error('Chemin fichier manquant');

  const name = fileName || path.split('/').pop() || 'file';

  if (!shouldDownloadViaProxy(path)) {
    const signed = await fetchSignedUrlDirect({ filePath: path, name });
    if (signed) {
      const link = document.createElement('a');
      link.href = signed;
      link.download = name;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
  }

  const fn = isStlPath(path, name) ? 'proxyStlFile' : 'proxyStorageFile';
  const blob = await fetchAuthenticatedBlob(fn, path, name, { download: '1' });
  triggerBlobDownload(blob, name);
}

/** Libère les blob URLs créées pour l’aperçu. */
export function revokePreviewBlobUrls(urlMap) {
  Object.values(urlMap || {}).forEach((u) => {
    if (typeof u === 'string' && u.startsWith('blob:')) {
      URL.revokeObjectURL(u);
    }
  });
}

async function fetchPreviewViaProxy(file) {
  const path = file.filePath || file.thumbnail;
  if (!path) return null;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return null;

  const params = new URLSearchParams({
    file_path: path,
    file_name: file.name || path.split('/').pop() || 'file',
  });

  const response = await fetch(
    `${supabase.supabaseUrl}/functions/v1/proxyStorageFile?${params}`,
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabase.supabaseKey,
      },
    }
  );

  if (!response.ok) return null;
  const blob = await response.blob();
  if (!blob.size) return null;
  return URL.createObjectURL(blob);
}

async function fetchSignedUrlDirect(file) {
  const path = file.filePath || file.thumbnail;
  if (!path) return null;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return null;

  try {
    const response = await fetch(
      `${supabase.supabaseUrl}/functions/v1/getSignedFileUrl?file_path=${encodeURIComponent(path)}&file_name=${encodeURIComponent(file.name || '')}`,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: supabase.supabaseKey,
        },
      }
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body?.signedUrl) return null;
    if (!isDirectBrowserUrl(body.signedUrl)) return null;
    return body.signedUrl;
  } catch {
    return null;
  }
}

/**
 * URLs d’aperçu pour images (photos) — CDN/S3 direct ou blob via proxy (Bunny HTTP).
 * @param {Array<{ id: string, filePath?: string, thumbnail?: string, name?: string }>} imageFiles
 * @returns {Promise<Record<string, string>>} id fichier → URL affichable
 */
export async function fetchSignedUrlsByFileId(imageFiles) {
  if (!imageFiles?.length) return {};

  const results = await Promise.all(
    imageFiles.map(async (f) => {
      const direct = await fetchSignedUrlDirect(f);
      if (direct) return { id: f.id, url: direct };

      const blobUrl = await fetchPreviewViaProxy(f);
      if (blobUrl) return { id: f.id, url: blobUrl };
      return null;
    })
  );

  const map = {};
  for (const r of results) {
    if (r?.id && r?.url) map[r.id] = r.url;
  }
  return map;
}

/** Mappe les URLs (par id) vers les clés photo (smileFront, …). */
export function mapSignedUrlsToPhotoKeys(uploadedPhotosByKey, signedById) {
  const out = {};
  for (const [key, rec] of Object.entries(uploadedPhotosByKey || {})) {
    if (rec?.id && signedById[rec.id]) out[key] = signedById[rec.id];
  }
  return out;
}
